import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { OidcSecurityService } from 'angular-auth-oidc-client'
import { Observable, throwError } from 'rxjs'
import { catchError, finalize, shareReplay, switchMap, take } from 'rxjs/operators'

/**
 * Auth-error handling for the admin API. Runs INSIDE errorTranslationInterceptor,
 * so `error.error.message` here is still the raw backend code.
 *
 * Backend staff-auth taxonomy (authenticateAuthentikJWT):
 *   439 JWT_EXPIRED          → the ONLY refreshable failure
 *   403 JWT/staff-token bad  → INVALID_TOKEN / INVALID_STAFF_TOKEN → re-auth
 *   403 FORBIDDEN_STAFF_GROUP→ authenticated but not staff → NOT refreshable,
 *                              and logging off just loops back through Authentik
 *   401 MISSING_BEARER_TOKEN → no token on the request → re-auth
 *   423                      → account locked → re-auth
 */

// Single-flight silent renew: concurrent 439s share ONE forceRefreshSession
// instead of each firing their own (which rotates refresh tokens, races the
// library's own silentRenew, and produces spurious logoffs). Reset when the
// renew settles so a later, genuine expiry triggers a fresh renew.
let inFlightRefresh: Observable<string | null> | null = null

function singleFlightRefresh(oidc: OidcSecurityService): Observable<string | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = oidc.forceRefreshSession().pipe(
      switchMap(() => oidc.getAccessToken().pipe(take(1))),
      finalize(() => (inFlightRefresh = null)),
      shareReplay(1),
    )
  }
  return inFlightRefresh
}

export const refreshTokenInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const oidcSecurityService = inject(OidcSecurityService)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Billing paywall — an app-layer concern, never touched here.
      if (error.status === 402) {
        return throwError(() => error)
      }

      // 439 JWT_EXPIRED — silently renew (coalesced), retry the request once,
      // and log off only if the renew fails or the retry still fails.
      if (error.status === 439) {
        return singleFlightRefresh(oidcSecurityService).pipe(
          switchMap(token => {
            if (!token) {
              oidcSecurityService.logoff().subscribe()
              return throwError(() => error)
            }
            return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
          }),
          catchError((retryError: HttpErrorResponse) => {
            oidcSecurityService.logoff().subscribe()
            return throwError(() => retryError)
          }),
        )
      }

      // 403s are never fixed by refreshing (same token comes back). A
      // not-a-staff-member (FORBIDDEN_STAFF_GROUP) is a valid session, so
      // surface the error and let the app show access-denied — logging off
      // would bounce them through Authentik straight back into the same 403.
      // A structurally bad token forces a clean re-auth.
      if (error.status === 403) {
        if (error.error?.message !== 'FORBIDDEN_STAFF_GROUP') {
          oidcSecurityService.logoff().subscribe()
        }
        return throwError(() => error)
      }

      // 401 (no bearer token reached the API) / 423 (locked) — re-authenticate.
      if (error.status === 401 || error.status === 423) {
        oidcSecurityService.logoff().subscribe()
        return throwError(() => error)
      }

      return throwError(() => error)
    }),
  )
}
